import { ImageView, View } from '@play-co/timestep-core/ui';
import i18n from 'src/lib/i18n/i18n';
import { Feature } from 'src/replicant/analytics';
import StateObserver from 'src/StateObserver';
import PopupMatch2Base from '../components/PopupMatch2Base';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import * as Match2 from 'src/replicant/match2';
import { dailyChallengesAssetConfig } from '../components/configs/dailyChallengesAssetConfig';
import Match2DailyChallengeBar from './Match2DailyChallengeBar';
import { Match2RewardsPanel } from '../components/Match2RewardsPanel';
import uiConfig from 'src/lib/ui/config';
import { Match2DailyChallengeTimer } from './Match2DailyChallengeTimer';
import sounds from 'src/lib/sounds';
import { Match2Reward } from 'src/replicant/match2/rewards/match2-rewards.ruleset';
import { AssetGroup } from '@play-co/timestep-core';
import type { Match2DailyChallengeId } from 'src/replicant/match2/dailychallenge/match2-dailychallenge.ruleset';

export default class PopupMatch2DailyChallenge extends PopupMatch2Base {
  private id: Match2DailyChallengeId;

  constructor(opts: { superview: View; close: (success: boolean) => void }) {
    const { user } = StateObserver.getState();
    const config = Match2.dailyChallengeGetters.getDailyChallengeConfig(user);
    if (!config) throw new Error('No daily challenge config found');
    const id = Match2.dailyChallengeGetters.getDailyChallengeId(user);
    if (!id) throw new Error('No daily challenge id found');
    const completed =
      Match2.dailyChallengeGetters.isDailyChallengeComplete(user);
    const rewards: Match2Reward[] = [
      { type: 'gems', amount: config.rewardGems },
    ];
    const locale = StateObserver.getLocale() === 'en' ? 'en' : 'jp';
    const title = dailyChallengesAssetConfig[id][`title_${locale}`];
    const splash = dailyChallengesAssetConfig[id].splash;

    super({
      superview: opts.superview,
      panelImageOpts: {
        ...uiConfig.popups.buruburu,
        width: 588,
        height: 859,
      },
      offsetY: -150,
      close: () => opts.close(false),
      title: { type: 'image', opts: { ...title, y: 15 } },
      cta: {
        localeText: () => i18n('match2.dailyBonus.claim').toLocaleUpperCase(),
        onClick: async () => {
          await rewardsPanel.grantRewards(this.root);
          opts.close(true);
        },
        positioning: 'edge',
        offsetY: 12,
      },
      tracking: {
        feature: Feature.MATCH2.DAILY_CHALLENGE._,
        $subFeature: Feature.MATCH2.DAILY_CHALLENGE.POPUP,
      },
    });

    this.id = id;
    this.buttonCta!.style.visible = completed;
    this.buttonClose!.style.visible = !completed;
    this.refreshClickOutsidePopup();

    if (completed) {
      sounds.sfx.play('applause');
    }

    new ImageView({
      superview: this.panel,
      ...splash,
      canHandleEvents: false,
      x: this.panel.style.width * 0.5,
      y: 260,
      centerOnOrigin: true,
      centerAnchor: true,
    });

    new LangBitmapFontTextView({
      superview: this.panel,
      localeText: () =>
        i18n(`match2.dailyChallenges.description_${id}`, {
          n: config.requirement,
        }),
      font: 'Body',
      color: '#BF8147',
      width: this.panel.style.width * 0.8,
      size: 26,
      x: this.panel.style.width * 0.5,
      y: 565,
      centerOnOrigin: true,
      centerAnchor: true,
      align: 'center',
      verticalAlign: 'center',
      wordWrap: true,
    });

    const bar = new Match2DailyChallengeBar({
      superview: this.panel,
      x: this.panel.style.width * 0.5 - 380 * 0.5,
      y: 480,
    });

    bar.setObjective(id);
    bar.setRequirement(config.requirement);
    bar.current = user.match2.dailyChallenge?.progress ?? 0;

    const rewardsPanel = new Match2RewardsPanel({
      superview: this.panel,
      height: 132,
      x: this.panel.style.width * 0.5,
      y: 735,
      theme: 'dark',
    });

    rewardsPanel.setRewards(rewards);

    const timer = new Match2DailyChallengeTimer({
      superview: this.panel,
      x: this.panel.style.width * 0.5,
      y: 455,
      centerOnOrigin: true,
      width: 146,
      height: 40,
    });

    timer.update(
      Match2.dailyChallengeGetters.getDailyChallengeRemainingTime(
        StateObserver.now(),
      ),
    );
  }

  protected getAssetsGroup() {
    const urls = AssetGroup.constructURLs([
      `assets/ui/match2/daily_challenges/${this.id}`,
    ]);
    return new AssetGroup(urls);
  }
}
